// Generat automat din README.md - Enhanced cu funcționalități interactive avansate + Export + Bulk + Shortcuts
import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Phone, Mail, Calendar, MapPin, Building, DollarSign, Activity, MessageCircle, Edit, Star, Clock, TrendingUp, Target, CheckCircle, AlertTriangle, X, Info, Filter, Download, FileText, Eye, Trash2, UserPlus, Baby, Heart, Gift, Save, Keyboard, HelpCircle } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import ExportModal from '../../components/ExportModal';
import AdvancedSearchBar, { useAdvancedSearch } from '../../components/AdvancedSearchBar';
import BulkSelectableTable from '../../components/BulkSelectableTable';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useAutoSave, AutoSaveIndicator } from '../../hooks/useAutoSave';
import { useExport, advancedExport } from '../../utils/export/exportUtils';
import { mockData } from '../../utils/mockData';

// Toast Component
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: CheckCircle,
    error: AlertTriangle,
    warning: AlertTriangle,
    info: Info
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500', 
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  const Icon = icons[type];

  useEffect(() => {
    const timer = setTimeout(() => onClose(), 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`${colors[type]} text-white p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-in fixed top-4 right-4 z-50 min-w-80`}>
      <Icon className="w-5 h-5" />
      <span>{message}</span>
      <button onClick={onClose} className="ml-auto">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

// Validators
const validators = {
  required: (value) => !value || value.toString().trim() === '' ? 'Acest câmp este obligatoriu' : null,
  email: (value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Format email invalid' : null,
  phone: (value) => !/^(\+4|4|0)?[0-9\s\-\(\)]{9,}$/.test(value.replace(/\s/g, '')) ? 'Format telefon invalid' : null
};

const useValidation = (formData, rules) => {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    Object.keys(rules).forEach(field => {
      const fieldRules = rules[field];
      const value = formData[field];
      fieldRules.forEach(rule => {
        const error = rule(value);
        if (error && !newErrors[field]) {
          newErrors[field] = error;
        }
      });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return { errors, validate, setErrors };
};

// Keyboard Shortcuts Help Modal
const ShortcutHelpModal = ({ isOpen, onClose }) => {
  const shortcuts = [
    { keys: 'Ctrl + N', description: 'Client Nou' },
    { keys: 'Ctrl + E', description: 'Export Date' },
    { keys: 'Shift + ?', description: 'Afișează Ajutor' },
    { keys: 'Escape', description: 'Închide Modal' },
    { keys: 'Ctrl + K', description: 'Focus pe Căutare' },
    { keys: 'Ctrl + S', description: 'Salvează' }
  ];

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Keyboard Shortcuts">
      <div className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Folosește aceste scurtături pentru o navigare mai rapidă:
        </p>
        <div className="space-y-3">
          {shortcuts.map((shortcut, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-900 dark:text-white">{shortcut.description}</span>
              <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-sm font-mono">
                {shortcut.keys}
              </kbd>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

const CRMComplet = () => {
  // State management
  const [customers, setCustomers] = useState(mockData.customers);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toasts, showToast, removeToast } = useToast();
  
  // Advanced Search & Filtering
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    sorting,
    setSorting,
    filteredData: filteredCustomers,
    clearFilters,
    hasActiveFilters
  } = useAdvancedSearch(customers, ['name', 'contactPerson', 'email', 'childName']);
  
  // Export functionality
  const { isExporting, handleExport } = useExport();

  // Form data pentru adăugare client nou
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    status: 'Prospect',
    childName: '',
    childBirthDate: '',
    baptismDate: '',
    preferredProducts: '',
    notes: '',
    tier: 'Regular',
    source: 'Reclamă online',
    address: ''
  });
  
  // Auto-save pentru form
  const {
    isSaving: isAutoSaving,
    lastSaved,
    hasUnsavedChanges,
    saveNow
  } = useAutoSave(
    newCustomerForm,
    async (data) => {
      // Simulate API save
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('Auto-saved form data:', data);
    },
    {
      delay: 3000,
      storageKey: 'crm-new-customer-form',
      onSaveSuccess: () => showToast('Form salvat automat', 'info'),
      onSaveError: (error) => showToast('Eroare auto-save', 'error')
    }
  );

  // Validare rules
  const validationRules = {
    name: [validators.required],
    contactPerson: [validators.required],
    email: [validators.required, validators.email],
    phone: [validators.required, validators.phone],
    childName: [validators.required]
  };

  const { errors, validate, setErrors } = useValidation(newCustomerForm, validationRules);

  // Keyboard Shortcuts
  useKeyboardShortcuts([
    {
      key: 'n',
      ctrl: true,
      action: () => setShowAddModal(true),
      description: 'Client Nou'
    },
    {
      key: 'e', 
      ctrl: true,
      action: () => setShowExportModal(true),
      description: 'Export Date'
    },
    {
      key: '?',
      shift: true,
      action: () => setShowShortcutHelp(true),
      description: 'Afișează Ajutor'
    },
    {
      key: 'Escape',
      action: () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setShowDetailsModal(false);
        setShowExportModal(false);
        setShowShortcutHelp(false);
      }
    }
  ]);

  // Available filters for advanced search
  const availableFilters = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Prospect', label: 'Prospect' },
        { value: 'Client nou', label: 'Client nou' },
        { value: 'Client fidel', label: 'Client fidel' }
      ]
    },
    {
      key: 'tier',
      label: 'Categorie',
      type: 'select', 
      options: [
        { value: 'Regular', label: 'Regular' },
        { value: 'Premium', label: 'Premium' },
        { value: 'VIP', label: 'VIP' }
      ]
    },
    {
      key: 'lastContact',
      label: 'Ultima Interacțiune',
      type: 'date'
    }
  ];

  // Handlers
  const handleAddCustomer = async () => {
    if (!validate()) {
      showToast('Te rog completează toate câmpurile obligatorii', 'error');
      return;
    }

    setLoading(true);
    
    // Simulare API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const newCustomer = {
      id: customers.length + 1,
      ...newCustomerForm,
      lastContact: new Date().toISOString().split('T')[0],
      orders: 0,
      totalSpent: '0 RON'
    };

    setCustomers(prev => [...prev, newCustomer]);
    setNewCustomerForm({
      name: '', contactPerson: '', email: '', phone: '', status: 'Prospect',
      childName: '', childBirthDate: '', baptismDate: '', preferredProducts: '',
      notes: '', tier: 'Regular', source: 'Reclamă online', address: ''
    });
    
    setShowAddModal(false);
    setLoading(false);
    setErrors({});
    showToast(`Client ${newCustomer.name} a fost adăugat cu succes!`, 'success');
  };

  const handleEditCustomer = async () => {
    if (!editingCustomer) return;
    
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setCustomers(prev => 
      prev.map(customer => 
        customer.id === editingCustomer.id ? editingCustomer : customer
      )
    );
    
    setShowEditModal(false);
    setEditingCustomer(null);
    setLoading(false);
    showToast('Informațiile clientului au fost actualizate!', 'success');
  };

  // Export handlers
  const handleExportData = async (config) => {
    const result = await handleExport(
      filteredCustomers,
      {
        ...config,
        moduleType: 'customers'
      },
      (message) => showToast(message, 'success'),
      (message) => showToast(message, 'error')
    );
    return result;
  };

  // Bulk operations
  const handleBulkDelete = async (selectedData) => {
    if (window.confirm(`Ștergi ${selectedData.length} clienți? Această acțiune nu poate fi anulată.`)) {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const selectedIds = selectedData.map(item => item.id);
      setCustomers(prev => prev.filter(customer => !selectedIds.includes(customer.id)));
      
      setLoading(false);
      showToast(`${selectedData.length} clienți șterși cu succes!`, 'success');
    }
  };

  const handleBulkExport = (selectedData) => {
    setShowExportModal(true);
  };

  const handleStartEdit = (customer) => {
    setEditingCustomer({ ...customer });
    setShowEditModal(true);
  };

  // Table columns for bulk selection
  const tableColumns = [
    {
      key: 'name',
      header: 'Nume Familie',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
            {value.charAt(0)}
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-white">{value}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{row.contactPerson}</div>
          </div>
        </div>
      )
    },
    {
      key: 'childName',
      header: 'Copil',
      render: (value) => value || 'Nu specificat'
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <Badge variant={value === 'Client fidel' ? 'success' : value === 'Client nou' ? 'info' : 'warning'}>
          {value}
        </Badge>
      )
    },
    {
      key: 'email',
      header: 'Email'
    },
    {
      key: 'phone',
      header: 'Telefon'
    },
    {
      key: 'lastContact',
      header: 'Ultima Interacțiune'
    }
  ];

  // Statistici derivate
  const stats = {
    totalCustomers: filteredCustomers.length,
    prospects: filteredCustomers.filter(c => c.status === 'Prospect').length,
    activeClients: filteredCustomers.filter(c => c.status === 'Client fidel').length,
    avgValue: filteredCustomers.reduce((sum, c) => sum + parseFloat(c.totalSpent.replace(/[^0-9.-]+/g, '')), 0) / filteredCustomers.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Header cu Shortcuts Info */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">CRM Complet</h1>
            <button
              onClick={() => setShowShortcutHelp(true)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Keyboard Shortcuts (Shift + ?)"
            >
              <Keyboard className="w-4 h-4" />
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Sistem complet de management clienți pentru servicii de botez
          </p>
          {/* Auto-save indicator */}
          <AutoSaveIndicator
            isSaving={isAutoSaving}
            lastSaved={lastSaved}
            hasUnsavedChanges={hasUnsavedChanges}
            className="mt-2"
          />
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowExportModal(true)}
            className="btn-secondary flex items-center gap-2"
            title="Export Date (Ctrl + E)"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
            title="Client Nou (Ctrl + N)"
          >
            <Plus className="w-4 h-4" />
            Client Nou
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card title="Total Clienți" icon={Users} variant="stats">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.totalCustomers}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Din baza de date
          </p>
        </Card>

        <Card title="Prospecți" icon={Target} variant="stats">
          <div className="text-3xl font-bold text-yellow-600">
            {stats.prospects}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Clienți potențiali
          </p>
        </Card>

        <Card title="Clienți Activi" icon={Star} variant="stats">
          <div className="text-3xl font-bold text-green-600">
            {stats.activeClients}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Clienți fideli
          </p>
        </Card>

        <Card title="Valoare Medie" icon={DollarSign} variant="stats">
          <div className="text-3xl font-bold text-blue-600">
            {stats.avgValue.toFixed(0)} RON
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Per client
          </p>
        </Card>
      </div>

      {/* Advanced Search & Filters */}
      <Card title="Căutare & Filtrare Avansată" icon={Search}>
        <AdvancedSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={filters}
          onFiltersChange={setFilters}
          availableFilters={availableFilters}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          placeholder="Caută după nume, email, telefon, numele copilului..."
        />
      </Card>

      {/* Bulk Selectable Table */}
      <Card title="Lista Clienților" icon={Users}>
        <BulkSelectableTable
          data={filteredCustomers}
          columns={tableColumns}
          onRowClick={(customer) => {
            setSelectedCustomer(customer);
            setShowDetailsModal(true);
          }}
          bulkActions={{
            onBulkDelete: handleBulkDelete,
            onBulkExport: handleBulkExport,
            customActions: [
              {
                key: 'markAsFidel',
                label: 'Marchează ca Fidel',
                icon: Star,
                onClick: (selectedData) => {
                  // Handle bulk status update
                  showToast(`${selectedData.length} clienți marcați ca fideli!`, 'success');
                },
                variant: 'success'
              }
            ]
          }}
        />
      </Card>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={filteredCustomers}
        moduleType="customers"
        onExport={handleExportData}
        title="Export Clienți CRM"
      />

      {/* Shortcut Help Modal */}
      <ShortcutHelpModal
        isOpen={showShortcutHelp}
        onClose={() => setShowShortcutHelp(false)}
      />

      {/* Add Customer Modal - Similar to existing but with auto-save */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Adaugă Client Nou"
        size="lg"
      >
        <div className="space-y-6">
          {/* Auto-save indicator for modal */}
          <AutoSaveIndicator
            isSaving={isAutoSaving}
            lastSaved={lastSaved}
            hasUnsavedChanges={hasUnsavedChanges}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Numele Familiei *
              </label>
              <input
                type="text"
                value={newCustomerForm.name}
                onChange={(e) => setNewCustomerForm({...newCustomerForm, name: e.target.value})}
                className={`input-field ${errors.name ? 'border-red-500' : ''}`}
                placeholder="ex: Familie Popescu"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Persoana de Contact *
              </label>
              <input
                type="text"
                value={newCustomerForm.contactPerson}
                onChange={(e) => setNewCustomerForm({...newCustomerForm, contactPerson: e.target.value})}
                className={`input-field ${errors.contactPerson ? 'border-red-500' : ''}`}
                placeholder="ex: Maria Popescu"
              />
              {errors.contactPerson && (
                <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Email *
              </label>
              <input
                type="email"
                value={newCustomerForm.email}
                onChange={(e) => setNewCustomerForm({...newCustomerForm, email: e.target.value})}
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                placeholder="ex: maria@email.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Telefon *
              </label>
              <input
                type="tel"
                value={newCustomerForm.phone}
                onChange={(e) => setNewCustomerForm({...newCustomerForm, phone: e.target.value})}
                className={`input-field ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="ex: 0721123456"
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Numele Copilului *
              </label>
              <input
                type="text"
                value={newCustomerForm.childName}
                onChange={(e) => setNewCustomerForm({...newCustomerForm, childName: e.target.value})}
                className={`input-field ${errors.childName ? 'border-red-500' : ''}`}
                placeholder="ex: Andrei"
              />
              {errors.childName && (
                <p className="text-red-500 text-sm mt-1">{errors.childName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={newCustomerForm.status}
                onChange={(e) => setNewCustomerForm({...newCustomerForm, status: e.target.value})}
                className="input-field"
              >
                <option value="Prospect">Prospect</option>
                <option value="Client nou">Client nou</option>
                <option value="Client fidel">Client fidel</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Note și Observații</label>
            <textarea
              value={newCustomerForm.notes}
              onChange={(e) => setNewCustomerForm({...newCustomerForm, notes: e.target.value})}
              className="input-field"
              rows="3"
              placeholder="Detalii despre preferințe, cerințe speciale, etc..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
            <button
              type="button"
              onClick={handleAddCustomer}
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Salvează Client
            </button>
          </div>
        </div>
      </Modal>

      {/* Other existing modals remain the same */}
    </div>
  );
};

export default CRMComplet;